import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Button,
  Input,
  VStack,
  Heading,
  useToast,
  Select,
  Spinner,
  Flex,
  HStack,
  Progress,
  Icon,
  IconButton,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon, CloseIcon, AttachmentIcon } from "@chakra-ui/icons";
import { FaCloudUploadAlt, FaBrain, FaList } from "react-icons/fa";
import { updateProjectContext } from "../../services/contextService";
import { CreditCostBadge } from "../CreditCostBadge";

const MotionBox = motion(Box);

interface DescriptionWizardProps {
  projectId: string | undefined;
  onComplete: () => void;
  onSkip: () => void;
  mode?: 'create' | 'analyze';
  skipBrandStep?: boolean;
}

const DescriptionWizard: React.FC<DescriptionWizardProps> = ({
  projectId,
  onComplete,
  onSkip,
  mode = 'create',
  skipBrandStep = false,
}) => {
  const { t } = useTranslation();
  const { currentUser, getToken } = useAuth();
  const { description, setDescription, setProductBrief, project } = useProject();
  const toast = useToast();

  const [step, setStep] = useState(skipBrandStep ? 2 : 0);
  const [brand, setBrand] = useState("");
  const [objectName, setObjectName] = useState("");
  const [userBrands, setUserBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Images
  const [images, setImages] = useState<{file: File, preview: string}[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Mode Selection (Questions vs Assertions)
  const [refinementMode, setRefinementMode] = useState<'questions' | 'assertions' | null>(null);

  // Logic
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [generatedQuestions, setGeneratedQuestions] = useState<string[]>([]);
  const [generatedAssertions, setGeneratedAssertions] = useState<string[]>([]);
  const [answer, setAnswer] = useState("");
  const [qaPairs, setQaPairs] = useState<{question: string, answer: string}[]>([]);
  const [assertionResults, setAssertionResults] = useState<{assertion: string, result: string, nuance?: string}[]>([]);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [showNuanceInput, setShowNuanceInput] = useState(false);
  const [nuanceText, setNuanceText] = useState("");

  const answerInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nuanceInputRef = useRef<HTMLInputElement>(null);
  const brandInputRef = useRef<HTMLInputElement>(null);
  const objectInputRef = useRef<HTMLInputElement>(null);

  // Fetch user brands
  useEffect(() => {
    if (currentUser) {
      // @ts-ignore
      if (currentUser.brands) {
        // @ts-ignore
        setUserBrands(currentUser.brands);
      }
    }
  }, [currentUser]);

  // Pre-fill object name, brand, and description (mode analyse)
  useEffect(() => {
    if (project) {
        if (project.data?.title) {
            setObjectName(project.data.title);
        } else if (project.name) {
            setObjectName(project.name);
        }
        
        const existingBrand = project.context?.brand || project.brand || project.data?.brand;
        if (existingBrand) {
            setBrand(existingBrand);
        }
    }
  }, [project]);
  
  // Skip intro steps if description exists (enrichment mode) OR if brand is already set
  useEffect(() => {
    if (step === 0) {
        const existingBrand = project?.context?.brand || project?.brand || project?.data?.brand;
        if (description) {
            setStep(2);
        } else if (existingBrand) {
            // If brand exists but no description, skip to object name (step 1)
            // But wait, we need to make sure 'brand' state is set. The previous useEffect does that.
            // Let's just check if 'brand' state is populated to be safe, or trust the prop.
            // Better to rely on the state being set by the other effect.
            // However, effects run after render. 
            // Let's just rely on the user clicking next if it's pre-filled, OR auto-skip if we want to be aggressive.
            // For now, let's just pre-fill. If description exists, we skip everything anyway.
        }
    }
  }, [description, project, step]);

  // Focus management
  useEffect(() => {
    if (step === 0 && brandInputRef.current) {
        setTimeout(() => brandInputRef.current?.focus(), 100);
    }
    if (step === 1 && objectInputRef.current) {
        setTimeout(() => objectInputRef.current?.focus(), 100);
    }
    if (answerInputRef.current && step === 3 && refinementMode === 'questions') {
      setTimeout(() => answerInputRef.current?.focus(), 100);
    }
    if (nuanceInputRef.current && showNuanceInput) {
      setTimeout(() => nuanceInputRef.current?.focus(), 100);
    }
  }, [step, currentQuestionIndex, refinementMode, showNuanceInput]);

  // Handle Paste for Images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (step !== 2) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const blob = items[i].getAsFile();
          if (blob) handleImageAdd(blob);
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [step]);

  const handleBrandSave = async () => {
    if (!brand) return;
    setUserBrands((prev) => Array.from(new Set([...prev, brand])));
    
    // Save to User's brand list
    try {
      const token = await getToken();
      await fetch("/.netlify/functions/saveUserBrand", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ brand }),
      });
      
      // Save to Project Context immediately
      await updateProjectContext({
          projectId,
          userId: currentUser?.uid,
          updates: { brand }
      });

    } catch (error) {
      console.error("Failed to save brand", error);
    }
  };

  const handleImageAdd = (file: File) => {
    const preview = URL.createObjectURL(file);
    setImages(prev => [...prev, { file, preview }]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      Array.from(e.dataTransfer.files).forEach(file => {
        if (file.type.startsWith("image/")) handleImageAdd(file);
      });
      e.dataTransfer.clearData();
    }
  };

  const uploadImages = async () => {
    if (images.length === 0) return;
    setIsUploading(true);
    try {
      const token = await getToken();
      for (const img of images) {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(img.file);
        });
        const base64String = await base64Promise;
        await fetch("/.netlify/functions/uploadProjectImage", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            projectId, userId: currentUser?.uid, imageBase64: base64String, filename: img.file.name,
          }),
        });
      }
    } catch (error) {
      console.error("Upload failed", error);
      toast({ title: t("error"), description: t("uploadError"), status: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  // --- QUESTIONS LOGIC ---

  const buildProductBriefForQuestions = (currentDescription?: string, extraQaPairs?: { question: string; answer: string }[]) => {
    const base = {
      baseInfo: { productName: objectName || "", brand: brand || "" },
      sellingPoints: [] as string[],
      painPoints: [] as { problem: string; solution?: string }[],
      features: [] as string[],
      freeformNotes: "" as string | undefined,
      qaPairs: undefined as { question: string; answer: string }[] | undefined,
    };
    let freeform = description || currentDescription || "";
    if (mode === "analyze" && project?.data?.feature_bullets?.length > 0 && !freeform) {
      freeform = `${t("bulletsFromAmazon") || "Bullets Amazon"}:\n${project.data.feature_bullets.join("\n\n")}`;
    }
    if (images.length > 0) {
      freeform += "\n\n--\n\nDescription supplémentaire à partir des images fournies :\n(Analyse en cours...)\n\n--\n";
    }
    if (freeform.trim()) base.freeformNotes = freeform.trim();
    const allQa = [...qaPairs, ...(extraQaPairs || [])];
    if (allQa.length > 0) base.qaPairs = allQa;
    return base;
  };

  const triggerQuestionGeneration = async (currentDescription?: string) => {
    setIsGenerating(true);
    const extraQa = answer.trim() && generatedQuestions[currentQuestionIndex]
      ? [{ question: generatedQuestions[currentQuestionIndex], answer: answer.trim() }]
      : [];
    const productBrief = buildProductBriefForQuestions(currentDescription, extraQa.length ? extraQa : undefined);
    const { productBriefToPrompt } = await import("../../utils/productBriefToPrompt");
    const descriptionToUse = productBriefToPrompt(productBrief as Parameters<typeof productBriefToPrompt>[0], t) || `${t("product")}: ${objectName}\n${t("brand")}: ${brand}`;

    try {
      const token = await getToken();
      await fetch("/.netlify/functions/saveProductBrief", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          projectId,
          userId: currentUser?.uid,
          productBrief,
          currentLanguage: "fr",
          skipQuestionGeneration: true,
        }),
      });

      const genResponse = await fetch("/.netlify/functions/generateQuestions-background", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          projectId,
          userId: currentUser?.uid,
          description: descriptionToUse,
          currentLanguage: "fr",
          mode,
          productBrief,
        }),
      });

      if (!genResponse.ok) {
        const errData = await genResponse.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to start question generation");
      }

      pollForQuestions();
    } catch (error: unknown) {
      console.error("Error triggering questions", error);
      setIsGenerating(false);
      toast({
        title: t("error"),
        description: error instanceof Error ? error.message : "Failed to generate questions",
        status: "error",
        isClosable: true,
      });
    }
  };

  const pollForQuestions = () => {
    const interval = setInterval(async () => {
      try {
        const token = await getToken();
        const response = await fetch("/.netlify/functions/getProject", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId: currentUser?.uid, projectId }),
        });
        const result = await response.json();
        
        if (result.project?.questions && result.project.questions.length > 0) {
          setGeneratedQuestions(result.project.questions);
          clearInterval(interval);
          setIsGenerating(false);
        }
      } catch (e) { console.error(e); }
    }, 2000);

    setTimeout(() => {
      clearInterval(interval);
      if (isGenerating) setIsGenerating(false);
    }, 60000);
  };

  // --- ASSERTIONS LOGIC ---

  const triggerAssertionsGeneration = async () => {
    setIsGenerating(true);
    let descriptionToUse = description;
    if (!descriptionToUse) {
        if (mode === 'analyze' && project?.data?.feature_bullets?.length > 0) {
            const bulletsText = Array.isArray(project.data.feature_bullets)
                ? project.data.feature_bullets.join('\n\n')
                : '';
            descriptionToUse = `${t("product")}: ${objectName}\n${t("brand")}: ${brand}\n\n${t("bulletsFromAmazon") || "Bullets Amazon"}:\n${bulletsText}`;
        } else {
            descriptionToUse = `${t("product")}: ${objectName}\n${t("brand")}: ${brand}`;
        }
    }
    
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/generateAssertions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
            projectId, 
            description: descriptionToUse,
            images: images.length > 0,
            mode,
        }),
      });
      
      const result = await response.json();
      if (result.assertions && result.assertions.length > 0) {
          setGeneratedAssertions(result.assertions);
      } else {
          toast({ title: t("info"), description: t("noAssertionsGenerated"), status: "info" });
      }
    } catch (error) {
      console.error("Error generating assertions", error);
      toast({ title: t("error"), status: "error" });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAssertionVote = (type: 'yes' | 'no' | 'nuance') => {
    const currentAssertion = generatedAssertions[currentQuestionIndex];
    
    if (type === 'nuance' && !showNuanceInput) {
        setShowNuanceInput(true);
        return; // Wait for text input
    }

    let resultString = type === 'yes' ? t("yes") : type === 'no' ? t("no") : t("itsComplicated");
    let finalNuance = type === 'nuance' ? nuanceText : undefined;

    setAssertionResults(prev => [...prev, { 
        assertion: currentAssertion, 
        result: resultString, 
        nuance: finalNuance 
    }]);

    // Reset nuance state
    setShowNuanceInput(false);
    setNuanceText("");

    if (currentQuestionIndex < generatedAssertions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
    } else {
        handleFinish(true); // Finish with assertions
    }
  };

  // --- NAVIGATION ---

  const nextStep = async () => {
    if (step === 0) {
      if (!brand) return;
      await handleBrandSave();
      setStep(1);
    } else if (step === 1) {
      if (!objectName) return;
      setStep(2); // Go to Images
    } else if (step === 2) {
      if (images.length > 0) {
        await uploadImages();
      }
      setStep(2.5); // Go to Mode Selection (Intermediate Step)
    }
  };

  const handleModeSelection = (mode: 'questions' | 'assertions') => {
      setRefinementMode(mode);
      setStep(3);
      if (mode === 'questions') {
          triggerQuestionGeneration();
      } else {
          triggerAssertionsGeneration();
      }
  };

  const handleFinish = async (fromAssertions = false) => {
    setLoading(true);
    let productBrief: {
      baseInfo: { productName: string; brand: string };
      sellingPoints: string[];
      painPoints: { problem: string; solution?: string }[];
      features: string[];
      freeformNotes?: string;
      qaPairs?: { question: string; answer: string }[];
      assertions?: { assertion: string; result: string; nuance?: string }[];
    } = {
      baseInfo: { productName: objectName || "", brand: brand || "" },
      sellingPoints: [],
      painPoints: [],
      features: [],
      freeformNotes: description ? description.trim() : undefined,
      qaPairs: qaPairs.length > 0 ? qaPairs : undefined,
      assertions: assertionResults.length > 0 ? assertionResults.map(ar => ({
        assertion: ar.assertion,
        result: ar.result,
        nuance: ar.nuance,
      })) : undefined,
    };

    if (images.length > 0) {
      const imgNote = "\n\n--\n\nDescription supplémentaire à partir des images fournies :\n(Analyse en cours...)\n\n--\n";
      productBrief.freeformNotes = (productBrief.freeformNotes || "") + imgNote;
    }

    try {
      const token = await getToken();

      if ((qaPairs.length > 0 || assertionResults.length > 0) && token) {
        try {
          const extractRes = await fetch(`/.netlify/functions/extractProductBrief`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              projectId,
              qaPairs,
              assertions: assertionResults.map(ar => ({
                assertion: ar.assertion,
                result: ar.result,
                nuance: ar.nuance,
              })),
            }),
          });
          if (extractRes.ok) {
            const extracted = await extractRes.json();
            if (extracted.painPoints?.length) productBrief.painPoints = extracted.painPoints;
            if (extracted.sellingPoints?.length) productBrief.sellingPoints = extracted.sellingPoints;
            if (extracted.features?.length) productBrief.features = extracted.features;
          }
        } catch (extractErr) {
          console.warn("Extract product brief failed, continuing with empty sections:", extractErr);
        }
      }

      if (objectName) {
        await fetch("/.netlify/functions/editProject", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ projectId, newName: objectName }),
        });
      }

      const { productBriefToPrompt } = await import("../../utils/productBriefToPrompt");
      const flattened = productBriefToPrompt(productBrief, t);
      setDescription(flattened);
      setProductBrief(productBrief);

      await fetch("/.netlify/functions/saveProductBrief", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          projectId,
          userId: currentUser?.uid,
          productBrief,
          skipQuestionGeneration: true,
          currentLanguage: "fr",
        }),
      });
      onComplete();
    } catch (error) {
      toast({ title: t("error"), status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = (saveCurrent: boolean) => {
    if (saveCurrent && answer.trim()) {
      const currentQ = generatedQuestions[currentQuestionIndex];
      setQaPairs(prev => [...prev, { question: currentQ, answer: answer.trim() }]);
    }
    setAnswer("");
    if (currentQuestionIndex < generatedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setStep(4);
    }
  };

  const handleGenerateMore = async () => {
    // Only for questions mode loop
    let currentDesc = "";
    
    if (description) {
        currentDesc = description;
    } else {
        currentDesc = `${t("product")}: ${objectName}\n${t("brand")}: ${brand}`;
    }

    if (images.length > 0) currentDesc += "\n\n--\n\nDescription supplémentaire à partir des images fournies :\n(Analyse en cours...)\n\n--\n";
    
    const allQa = [...qaPairs];
    if (answer.trim()) allQa.push({ question: generatedQuestions[currentQuestionIndex], answer: answer.trim() });
    
    if (allQa.length > 0) {
      currentDesc += "\n\n" + t("details") + ":\n";
      allQa.forEach(qa => currentDesc += `- ${qa.question} ${qa.answer}\n`);
    }

    setGeneratedQuestions([]);
    setCurrentQuestionIndex(0);
    setStep(3);
    await triggerQuestionGeneration(currentDesc);
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95, display: "none" },
    visible: { opacity: 1, scale: 1, display: "block", transition: { duration: 0.5 } },
    exit: { opacity: 0, scale: 1.05, transition: { duration: 0.3 }, transitionEnd: { display: "none" } },
  };

  const handleSkip = () => {
    // If we have brand or objectName entered, we should save them before skipping
    if (brand || objectName) {
        // We trigger a background update but don't wait for it to close the modal
        const updatePromise = async () => {
            try {
                const token = await getToken();
                if (objectName) {
                    await fetch("/.netlify/functions/editProject", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ projectId, newName: objectName })
                    });
                }
                
                if (brand) {
                    await updateProjectContext({
                        projectId,
                        userId: currentUser?.uid,
                        updates: { brand }
                    });
                }
            } catch (e) {
                console.error("Error saving partial wizard data", e);
            }
        };
        updatePromise();
    }
    onSkip();
  };

  return (
    <Box position="fixed" top="0" left="0" right="0" bottom="0" bg="gray.900" zIndex="2000" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box position="absolute" top="4" right="4">
        <Button variant="ghost" color="whiteAlpha.700" onClick={handleSkip}>
          {t("skip")} <CloseIcon ml={2} w={3} h={3} />
        </Button>
      </Box>

      <AnimatePresence mode="wait">
        <MotionBox key={step} variants={containerVariants} initial="hidden" animate="visible" exit="exit" width="100%" maxWidth="600px">
          
          {/* LOADING STATE (Global) */}
          {loading && (
             <MotionBox 
               display="flex" 
               flexDirection="column" 
               alignItems="center" 
               justifyContent="center" 
               h="400px" 
               animate={{ opacity: 1 }} 
               initial={{ opacity: 0 }}
             >
               <VStack spacing={6}>
                 <Spinner color="teal.500" size="xl" thickness="4px" />
                 <Heading size="lg" color="white" textAlign="center">{t("finalizingBrief") || "Création de votre fiche produit..."}</Heading>
                 <Text color="whiteAlpha.700" textAlign="center" maxW="md">
                   {t("analyzingResponses") || "Nous analysons vos réponses pour générer les meilleurs arguments de vente et pain points."}
                 </Text>
                 <Text color="whiteAlpha.500" fontSize="sm" mt={4}>{t("pleaseWait")}</Text>
               </VStack>
             </MotionBox>
          )}

          {/* STEP 0: BRAND */}
          {!loading && step === 0 && (
            <VStack spacing={8} align="stretch">
              <Heading color="white" size="2xl" textAlign="center">{t("whatIsYourBrand")}</Heading>
              <VStack spacing={4}>
                {userBrands.length > 0 && (
                  <Select placeholder={t("selectExistingBrand")} bg="white" color="gray.800" onChange={(e) => setBrand(e.target.value)} value={userBrands.includes(brand) ? brand : ""} sx={{ "> option": { background: "white", color: "gray.800" } }}>
                    {userBrands.map((b, i) => <option key={i} value={b} style={{ color: "#333" }}>{b}</option>)}
                  </Select>
                )}
                <Input ref={brandInputRef} placeholder={t("enterBrandName")} size="lg" bg="whiteAlpha.100" color="white" border="none" _focus={{ bg: "whiteAlpha.200" }} value={brand} onChange={(e) => setBrand(e.target.value)} onKeyDown={(e) => e.key === "Enter" && nextStep()} />
              </VStack>
              <Button size="lg" colorScheme="teal" onClick={nextStep} isDisabled={!brand} rightIcon={<ChevronRightIcon />}>{t("next")}</Button>
            </VStack>
          )}

          {/* STEP 1: OBJECT */}
          {!loading && step === 1 && (
            <VStack spacing={8} align="stretch">
              <Heading color="white" size="2xl" textAlign="center">{t("whatObjectIsIt")}</Heading>
              <Input ref={objectInputRef} placeholder={t("productName")} size="lg" bg="whiteAlpha.100" color="white" border="none" _focus={{ bg: "whiteAlpha.200" }} value={objectName} onChange={(e) => setObjectName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && nextStep()} />
              <Button size="lg" colorScheme="teal" onClick={nextStep} isDisabled={!objectName} rightIcon={<ChevronRightIcon />}>{t("next")}</Button>
            </VStack>
          )}

          {/* STEP 2: IMAGES UPLOAD */}
          {!loading && step === 2 && (
            <VStack spacing={8} align="stretch">
              <Heading color="white" size="2xl" textAlign="center">{t("addImages")}</Heading>
              <Box border="2px dashed" borderColor="whiteAlpha.400" borderRadius="xl" p={10} textAlign="center" bg="whiteAlpha.50" transition="all 0.2s" cursor="pointer" _hover={{ bg: "whiteAlpha.100", borderColor: "teal.400" }} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
                <input type="file" multiple accept="image/*" hidden ref={fileInputRef} onChange={(e) => { if (e.target.files) Array.from(e.target.files).forEach(file => handleImageAdd(file)); }} />
                <VStack spacing={3}>
                  <Icon as={FaCloudUploadAlt} w={10} h={10} color="teal.300" />
                  <Text color="gray.300" fontSize="lg">{t("dropImagesHere")}</Text>
                  <Text color="gray.500" fontSize="sm">{t("browse")}</Text>
                </VStack>
              </Box>
              {images.length > 0 && (
                <VStack align="start" spacing={2}>
                  <Text color="whiteAlpha.700" fontSize="sm">{t("imagesAdded", { count: images.length })}</Text>
                  <Flex wrap="wrap" gap={2}>
                    {images.map((img, i) => (
                      <Box key={i} position="relative" w="80px" h="80px" borderRadius="md" overflow="hidden">
                        <img src={img.preview} alt="preview" style={{width: "100%", height: "100%", objectFit: "cover"}} />
                        <IconButton aria-label="remove" icon={<CloseIcon />} size="xs" position="absolute" top="2px" right="2px" colorScheme="red" onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))} />
                      </Box>
                    ))}
                  </Flex>
                </VStack>
              )}
              <Button size="lg" colorScheme="teal" onClick={nextStep} rightIcon={isUploading ? <Spinner size="sm" /> : <ChevronRightIcon />} isLoading={isUploading} loadingText={t("uploading")}>{t("next")}</Button>
            </VStack>
          )}

          {/* STEP 2.5: CHOICE (NEW) */}
          {!loading && step === 2.5 && (
             <VStack spacing={8} align="stretch" textAlign="center">
                <Heading color="white" size="2xl">{t("chooseRefinementMethod")}</Heading>
                <SimpleGrid columns={{base: 1, md: 2}} spacing={6} pt={4}>
                    <Box 
                        bg="whiteAlpha.100" p={6} borderRadius="xl" cursor="pointer"
                        _hover={{ bg: "whiteAlpha.200", transform: "translateY(-5px)" }}
                        transition="all 0.2s"
                        onClick={() => handleModeSelection('questions')}
                    >
                        <VStack spacing={4}>
                            <Icon as={FaList} w={12} h={12} color="teal.300" />
                            <Heading size="md" color="white">{t("classicQuestions")}</Heading>
                            <Text color="gray.400" fontSize="sm">{t("classicQuestionsDesc")}</Text>
                        </VStack>
                    </Box>
                    
                    <Box 
                        bg="whiteAlpha.100" p={6} borderRadius="xl" cursor="pointer"
                        _hover={{ bg: "whiteAlpha.200", transform: "translateY(-5px)" }}
                        transition="all 0.2s"
                        onClick={() => handleModeSelection('assertions')}
                    >
                        <VStack spacing={4}>
                            <Icon as={FaBrain} w={12} h={12} color="purple.300" />
                            <Heading size="md" color="white">
                                {t("analysisMode")}
                                <CreditCostBadge actionKey="assertions" />
                            </Heading>
                            <Text color="gray.400" fontSize="sm">{t("analysisModeDesc")}</Text>
                        </VStack>
                    </Box>
                </SimpleGrid>
             </VStack>
          )}

          {/* STEP 3: REFINEMENT LOOPS (Questions OR Assertions) */}
          {!loading && step === 3 && (
            <VStack spacing={6} align="stretch">
              {isGenerating ? (
                 <Flex direction="column" align="center" justify="center" h="300px">
                   <Spinner color="teal.500" size="xl" mb={6} />
                   <Heading size="md" color="whiteAlpha.900" textAlign="center" mb={2}>{t("analyzingProduct")}</Heading>
                   <Text color="whiteAlpha.600">{t("pleaseWait")}</Text>
                 </Flex>
              ) : (
                <>
                {/* --- QUESTIONS MODE --- */}
                {refinementMode === 'questions' && generatedQuestions.length > 0 && (
                    <VStack spacing={8} align="stretch">
                       <Box>
                         <HStack justify="space-between" mb={2}>
                           <Text color="teal.400" fontSize="sm" fontWeight="bold">{t("questionProgress", { current: currentQuestionIndex + 1, total: generatedQuestions.length })}</Text>
                           <Button variant="link" color="whiteAlpha.600" size="sm" onClick={() => setStep(4)}>{t("finishEarly")}</Button>
                         </HStack>
                         <Progress value={((currentQuestionIndex) / generatedQuestions.length) * 100} size="xs" colorScheme="teal" borderRadius="full" />
                       </Box>
                       <AnimatePresence mode="wait">
                         <MotionBox key={currentQuestionIndex} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                           <Heading size="lg" color="white" mb={6}>{generatedQuestions[currentQuestionIndex]}</Heading>
                           <Input ref={answerInputRef} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t("yourAnswer")} size="lg" bg="whiteAlpha.100" color="white" border="none" _focus={{ bg: "whiteAlpha.200" }} onKeyDown={(e) => { if (e.key === "Enter") { if (e.metaKey || e.ctrlKey) handleNextQuestion(false); else handleNextQuestion(true); }}} autoFocus />
                           <Flex justify="space-between" mt={4} align="center">
                              <Text fontSize="xs" color="gray.500"><kbd>Enter</kbd> {t("validate")} • <kbd>Cmd</kbd> + <kbd>Enter</kbd> {t("skip")}</Text>
                              <HStack>
                                <Button variant="ghost" color="whiteAlpha.600" onClick={() => handleNextQuestion(false)}>{t("skip")}</Button>
                                <Button colorScheme="teal" onClick={() => handleNextQuestion(true)} isDisabled={!answer.trim()}>{t("validate")}</Button>
                              </HStack>
                           </Flex>
                         </MotionBox>
                       </AnimatePresence>
                    </VStack>
                )}

                {/* --- ASSERTIONS MODE --- */}
                {refinementMode === 'assertions' && generatedAssertions.length > 0 && (
                    <VStack spacing={8} align="stretch">
                       <Box>
                         <HStack justify="space-between" mb={2}>
                           <Text color="purple.400" fontSize="sm" fontWeight="bold">{t("assertionProgress", { current: currentQuestionIndex + 1, total: generatedAssertions.length })}</Text>
                           <Button variant="link" color="whiteAlpha.600" size="sm" onClick={() => handleFinish(true)}>{t("finishEarly")}</Button>
                         </HStack>
                         <Progress value={((currentQuestionIndex) / generatedAssertions.length) * 100} size="xs" colorScheme="purple" borderRadius="full" />
                       </Box>
                       <AnimatePresence mode="wait">
                         <MotionBox key={currentQuestionIndex} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.1 }}>
                           <Box bg="whiteAlpha.100" p={8} borderRadius="2xl" textAlign="center" mb={6}>
                               <Heading size="lg" color="white">{generatedAssertions[currentQuestionIndex]}</Heading>
                           </Box>
                           
                           {showNuanceInput ? (
                               <VStack spacing={4}>
                                   <Input ref={nuanceInputRef} value={nuanceText} onChange={(e) => setNuanceText(e.target.value)} placeholder={t("explainNuance")} bg="whiteAlpha.100" color="white" border="none" autoFocus onKeyDown={(e) => e.key === "Enter" && handleAssertionVote('nuance')} />
                                   <Button colorScheme="blue" onClick={() => handleAssertionVote('nuance')}>{t("validate")}</Button>
                               </VStack>
                           ) : (
                               <SimpleGrid columns={3} spacing={4}>
                                   <Button height="60px" colorScheme="red" fontSize="lg" onClick={() => handleAssertionVote('no')}>{t("no")}</Button>
                                   <Button height="60px" colorScheme="yellow" fontSize="sm" whiteSpace="normal" onClick={() => handleAssertionVote('nuance')}>{t("itsComplicated")}</Button>
                                   <Button height="60px" colorScheme="green" fontSize="lg" onClick={() => handleAssertionVote('yes')}>{t("yes")}</Button>
                               </SimpleGrid>
                           )}
                         </MotionBox>
                       </AnimatePresence>
                    </VStack>
                )}

                {/* Error/Empty State */}
                {!isGenerating && ((refinementMode === 'questions' && generatedQuestions.length === 0) || (refinementMode === 'assertions' && generatedAssertions.length === 0)) && (
                    <Flex direction="column" align="center" justify="center" h="300px">
                      <Text color="whiteAlpha.600" mb={4}>{t("noQuestionsGenerated")}</Text>
                      <Button size="md" variant="outline" colorScheme="teal" onClick={() => refinementMode === 'questions' ? triggerQuestionGeneration() : triggerAssertionsGeneration()}>{t("retry")}</Button>
                    </Flex>
                )}
                </>
              )}
            </VStack>
          )}

          {/* STEP 4: CONFIRMATION / RE-LOOP (Only for Questions mode usually, assertions end process) */}
          {!loading && step === 4 && (
            <VStack spacing={8} align="stretch" textAlign="center">
              <Heading color="white" size="2xl">{t("wantMoreQuestions")}</Heading>
              <Text color="gray.300" fontSize="lg">{t("wantMoreQuestionsDesc")}</Text>
              <VStack spacing={4} pt={4}>
                <Button size="lg" colorScheme="teal" onClick={handleGenerateMore} width="100%">{t("generateMoreQuestions")}</Button>
                <Button size="lg" variant="outline" colorScheme="whiteAlpha" color="white" onClick={() => handleFinish(false)} width="100%" isLoading={loading}>{t("finishNow")}</Button>
              </VStack>
            </VStack>
          )}

        </MotionBox>
      </AnimatePresence>
    </Box>
  );
};

export default DescriptionWizard;
